import { ComponentMultiStyleConfig, SystemStyleObject } from '@chakra-ui/react';
import type { StyleFunctionProps } from '@chakra-ui/theme-tools';
import { mode } from '@chakra-ui/theme-tools';

import { FONT_WEIGHTS } from '../../theme/Typography';

import { SideBarItemProps } from './SideBarItem';

const getScrollBarStyles = (props: StyleFunctionProps): SystemStyleObject => ({
  // FireFox Scrollbar:
  scrollbarColor: `${mode('rgba(0, 0, 0, 0.64)', 'rgba(255, 255, 255, 0.64)')(props)} transparent`,
  scrollbarWidth: 'thin',
  // Webkit scrollbar (Chrome, Safari, Edge):
  '::-webkit-scrollbar': {
    width: '8px',
    borderRadius: '8px'
  },
  '::-moz-scrollbar': {
    width: '8px',
    borderRadius: '8px'
  },
  '::-webkit-scrollbar-track': {
    background: 'transparent'
  },
  '::-moz-scrollbar-track': {
    background: 'transparent'
  },
  '::-webkit-scrollbar-thumb': {
    background: mode('rgba(0, 0, 0, 0.64)', 'rgba(255, 255, 255, 0.64)')(props),
    borderRadius: '8px'
  },
  '::-moz-scrollbar-thumb': {
    background: mode('rgba(0, 0, 0, 0.64)', 'rgba(255, 255, 255, 0.64)')(props),
    borderRadius: '8px'
  },
  '::-webkit-scrollbar-thumb:hover': {
    cursor: 'pointer'
  },
  '::-moz-scrollbar-thumb:hover': {
    cursor: 'pointer'
  }
});

const TOP_BAR_HEIGHT = '56px';

const activeStyles = {
  background: { base: '', md: 'blue.base' },
  color: { base: '', md: 'silver.lightest' }
};

const getHoverAndFocusStyles = (props: SideBarItemProps & StyleFunctionProps) => ({
  background: props.isActive ? activeStyles.background : mode('silver.light', 'black.lightest')(props),
  color: props.isActive ? activeStyles.color : mode('black.base', 'silver.lightest')(props),
  cursor: 'pointer'
});

const getSideBarItemSharedStyles = (props: SideBarItemProps & StyleFunctionProps) => {
  const { isActive } = props;

  const sharedStyles = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    color: mode('black.lighter', 'grey.lightest')(props),
    fontSize: '14px',
    padding: '0px 8px',
    minHeight: '32px',
    height: '32px',
    width: { base: '100%', md: '224px' },
    maxWidth: { base: '100%', md: '224px' },
    lineHeight: '32px',
    borderRadius: '8px',
    marginBottom: '2px',
    outline: 0,
    _hover: getHoverAndFocusStyles(props),
    _active: activeStyles,
    _focus: getHoverAndFocusStyles(props),
    _focusVisible: getHoverAndFocusStyles(props)
  };

  return isActive ? { ...sharedStyles, ...activeStyles } : sharedStyles;
};

const sideBarElementSharedStyles = {
  display: 'flex',
  alignItems: 'center',
  height: '32px'
};

const SideBarTheme: ComponentMultiStyleConfig = {
  parts: [
    'sideBar',
    'sideBarFooter',
    'sideBarHeading',
    'sideBarItem',
    'sideBarItemGroup',
    'sideBarItemLeftElement',
    'sideBarItemRightElement',
    'sideBarSection',
    'sideBarSectionAction',
    'sideBarSectionTitle'
  ],
  baseStyle: (props: StyleFunctionProps) => ({
    sideBar: {
      display: 'flex',
      flexDirection: 'column',
      flexBasis: '100%',
      alignItems: 'center',
      padding: '28px 16px 24px 16px',
      height: `calc(100vh - ${TOP_BAR_HEIGHT})`,
      width: { base: '100%', md: '256px' },
      minWidth: { base: '100%', sm: '0' },
      maxWidth: { base: '100%', md: '256px' },
      overflowY: 'scroll',
      overflowX: 'hidden',
      ...getScrollBarStyles(props),
      backgroundColor: mode('silver.lightest', 'black.lighter')(props),
      borderWidth: { base: 0, md: '1px' },
      borderTopColor: 'transparent',
      borderRightColor: mode('silver.base', 'black.lighter')(props),
      borderBottomColor: 'transparent',
      borderLeftColor: 'transparent'
    },
    sideBarHeading: {
      fontSize: '17px',
      fontWeight: FONT_WEIGHTS.semibold,
      height: '26px',
      minWidth: '208px',
      width: '100%',
      marginBottom: '18px',
      color: mode('black.base', 'silver.lightest')(props)
    },
    sideBarSection: {
      margin: '8px 0',
      borderTopWidth: '1px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'start',
      width: { base: '100%', md: 'calc(100% + 8px)' }
    },
    sideBarSectionTitle: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      color: mode('black.lighter', 'silver.light')(props),
      fontSize: '10px',
      fontWeight: FONT_WEIGHTS.semibold,
      letterSpacing: '0.8px',
      textTransform: 'uppercase',
      lineHeight: '32px',
      height: '32px',
      margin: '0 0 6px 8px'
    },
    sideBarItemGroup: getSideBarItemSharedStyles(props),
    sideBarItemLeftElement: sideBarElementSharedStyles,
    sideBarItem: getSideBarItemSharedStyles(props),
    sideBarItemRightElement: {
      ...sideBarElementSharedStyles,
      marginLeft: 'auto'
    },
    sideBarFooter: {
      display: 'flex',
      color: mode('black.lighter', 'grey.lightest')(props),
      height: '32px',
      maxWidth: { base: '100%', md: '224px' },
      width: { base: '100%', md: '224px' },
      alignItems: 'center',
      marginTop: 'auto',
      fontSize: '14px',
      paddingLeft: '4px',
      _hover: {
        cursor: 'pointer'
      }
    }
  }),
  defaultProps: {}
};

export default SideBarTheme;
